import { Card, Input, Radio, Select } from 'antd';
import { Info } from 'lucide-react';
import { Trans } from 'react-i18next';
import LOCALS from 'commons/locals';
import {
  Control,
  Controller,
  useWatch,
  UseFormSetValue,
} from 'react-hook-form';
import {
  CreateRecyclingConsignmentOrderV2SchemaType,
  recycleConsignmentStoreList,
} from '../common';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { OMS_RECYCLE_ORDER_TYPE_MAP } from 'commons/options';
import i18n from 'i18n';
import { useEffect, useMemo, useRef } from 'react';
import { getSelectableStaffOptions } from 'pages/rrs/recycling-consignment-detail/modify-base-info.utils';
import { SHOP_MAP } from 'constant/shop';
import {
  needsManualTaxRegimeSelection,
  ProductTaxRegime,
  resolveProductTaxRegime,
  SupplierType,
} from 'constants/france-tax';

const getTaxRegimeLabel = (taxRegime?: ProductTaxRegime) => {
  if (taxRegime === ProductTaxRegime.STANDARD_TVA) {
    return i18n.t(LOCALS.product_tax_regime_standard_tva);
  }
  if (taxRegime === ProductTaxRegime.MARGIN) {
    return i18n.t(LOCALS.product_tax_regime_margin);
  }
  return i18n.t(LOCALS.product_tax_regime_pending);
};

const BaseInfo = ({
  control,
  setValue,
}: {
  control: Control<CreateRecyclingConsignmentOrderV2SchemaType>;
  setValue: UseFormSetValue<CreateRecyclingConsignmentOrderV2SchemaType>;
}) => {
  const { staffSelectOptions } = useAppSelector(selectGlobalInfo);
  const activeStaffOptions = useMemo(
    () => getSelectableStaffOptions(staffSelectOptions),
    [staffSelectOptions],
  );
  const staffName = useWatch({
    control,
    name: 'staffName',
  });
  const receptionistId = useWatch({
    control,
    name: 'receptionistId',
  });
  const storeId = useWatch({
    control,
    name: 'storeId',
  });
  const orderType = useWatch({
    control,
    name: 'type',
  });
  const supplierType = useWatch({
    control,
    name: 'supplierType',
  });
  const taxRegime = useWatch({
    control,
    name: 'taxRegime',
  });
  const hasSyncedReceptionistRef = useRef(false);
  const isFranceStore = storeId === SHOP_MAP.FRANCE;
  const isManualTaxRegime = needsManualTaxRegimeSelection(
    orderType,
    supplierType,
  );
  const autoTaxRegime = resolveProductTaxRegime(orderType, supplierType);

  useEffect(() => {
    if (hasSyncedReceptionistRef.current) return;
    if (receptionistId !== undefined) {
      hasSyncedReceptionistRef.current = true;
      return;
    }

    const matchedStaff = activeStaffOptions.find(
      (option) => option.label === staffName,
    );
    if (matchedStaff && typeof matchedStaff.value === 'number') {
      setValue('receptionistId', matchedStaff.value);
      hasSyncedReceptionistRef.current = true;
    }
  }, [activeStaffOptions, receptionistId, setValue, staffName]);

  useEffect(() => {
    if (!isFranceStore && supplierType) {
      setValue('supplierType', null);
    }
  }, [isFranceStore, setValue, supplierType]);

  // 非「回收+法人」时清空手动税制；切回该场景时保持待选
  useEffect(() => {
    if (!isFranceStore || !isManualTaxRegime) {
      if (taxRegime) {
        setValue('taxRegime', null);
      }
    }
  }, [isFranceStore, isManualTaxRegime, setValue, taxRegime]);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <Info className="w-5 h-5" />
        <Trans i18nKey={LOCALS.basic_info} />
      </div>

      <div className="grid grid-cols-4 gap-8">
        <div className="flex items-center gap-2">
          <label className="required-field">
            {i18n.t(LOCALS.contract_type)}:
          </label>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                optionType="button"
                buttonStyle="solid"
                options={[
                  {
                    label: i18n.t(LOCALS.sell),
                    value: OMS_RECYCLE_ORDER_TYPE_MAP.RECYCLE,
                  },
                  {
                    label: i18n.t(LOCALS.consignment),
                    value: OMS_RECYCLE_ORDER_TYPE_MAP.CONSIGNMENT,
                  },
                ]}
              />
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="shrink-0">{i18n.t(LOCALS.reception_staff)}:</label>
          <Controller
            name="receptionistId"
            control={control}
            render={({ field }) => (
              <Select
                className="w-full"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.label || '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                {...field}
                options={activeStaffOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
              />
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="required-field shrink-0">
            {i18n.t(LOCALS.shop)}:
          </label>
          <Controller
            name="storeId"
            control={control}
            render={({ field }) => {
              return (
                <Select
                  {...field}
                  options={recycleConsignmentStoreList}
                  className="w-full"
                  placeholder={i18n.t(LOCALS.please_select)}
                />
              );
            }}
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="required-field shrink-0">
            {i18n.t(LOCALS.staff)}:
          </label>
          <Controller
            name="staffName"
            control={control}
            render={({ field }) => (
              <Select
                className="w-full"
                showSearch
                filterOption={(input, option) =>
                  String(option?.label || '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                {...field}
                options={activeStaffOptions.map((option) => ({
                  value: option.label,
                  label: option.label,
                }))}
              />
            )}
          />
        </div>
      </div>

      {isFranceStore && (
        <div className="grid grid-cols-4 gap-8 mt-4">
          <div className="flex items-center gap-2">
            <label className="required-field shrink-0">
              {i18n.t(LOCALS.supplier_type)}:
            </label>
            <Controller
              name="supplierType"
              control={control}
              render={({ field, formState: { errors } }) => (
                <div className="flex flex-col gap-1">
                  <Radio.Group
                    value={field.value ?? undefined}
                    onChange={(e) => field.onChange(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    options={[
                      {
                        label: i18n.t(LOCALS.supplier_type_individual),
                        value: SupplierType.INDIVIDUAL,
                      },
                      {
                        label: i18n.t(LOCALS.supplier_type_company),
                        value: SupplierType.COMPANY,
                      },
                    ]}
                  />
                  {errors.supplierType && (
                    <span className="text-red-500 text-sm">
                      {errors.supplierType.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>

          <div className="flex items-center gap-2 col-span-2">
            <label
              className={`shrink-0 ${isManualTaxRegime ? 'required-field' : ''}`}
            >
              {i18n.t(LOCALS.product_tax_regime)}:
            </label>
            {isManualTaxRegime ? (
              <Controller
                name="taxRegime"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <div className="flex flex-col gap-1">
                    <Radio.Group
                      value={field.value ?? undefined}
                      onChange={(e) => field.onChange(e.target.value)}
                      optionType="button"
                      buttonStyle="solid"
                      options={[
                        {
                          value: ProductTaxRegime.STANDARD_TVA,
                          label: i18n.t(LOCALS.product_tax_regime_standard_tva),
                        },
                        {
                          value: ProductTaxRegime.MARGIN,
                          label: i18n.t(LOCALS.product_tax_regime_margin),
                        },
                      ]}
                    />
                    {errors.taxRegime && (
                      <span className="text-red-500 text-sm">
                        {errors.taxRegime.message}
                      </span>
                    )}
                  </div>
                )}
              />
            ) : (
              <span className="text-gray-700">
                {getTaxRegimeLabel(autoTaxRegime)}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="flex gap-2">
          <label className="shrink-0">{i18n.t(LOCALS.service_comments)}:</label>
          <Controller
            name="baseShopRemark"
            control={control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                placeholder={i18n.t(LOCALS.please_enter) || ''}
                autoSize={{ minRows: 4, maxRows: 8 }}
                maxLength={500}
                showCount
                className="flex-1"
              />
            )}
          />
        </div>
      </div>
    </Card>
  );
};

export default BaseInfo;
